export class ToWords {
  private options: ToWordsOptions = {};
  private locale: LocaleInterface | undefined = undefined;

  constructor(options: ToWordsOptions = {}) {
    this.options = Object.assign(
      {
        localeCode: "en-IN",
        converterOptions: DefaultConverterOptions,
      },
      options
    );
  }

  getLocale(): LocaleInterface {
    if (this.locale === undefined) {
      this.locale = new Locale();
    }
    return this.locale as LocaleInterface;
  }

  convert(number: number, options: ConverterOptions = {}): string {
    options = Object.assign({}, this.options.converterOptions, options);

    if (!this.isValidNumber(number)) {
      //throw new Error(`Invalid Number "${number}"`);
      return "";
    }

    const locale = this.getLocale();

    let isFloat = this.isFloat(number);
    if (options.ignoreDecimal) {
      number = Number.parseInt(number.toString());
      isFloat = false;
    }

    const isNegativeNumber = number < 0;
    if (isNegativeNumber) {
      number = Math.abs(number);
    }

    if (options.currency) {
      number = this.toFixed(number);
      // Extra check for isFloat to overcome 1.999 rounding off to 2
      isFloat = this.isFloat(number);
      const split = number.toString().split(".");
      let words = `${this.convertInternal(Number(split[0]), options)}${
        locale.currency.plural ? ` ${locale.currency.plural}` : ""
      }`;
      const isNumberZero = number >= 0 && number < 1;
      const ignoreZero =
        options.ignoreZeroCurrency ||
        (locale.options?.ignoreZeroInDecimals && number !== 0);

      if (isNumberZero && ignoreZero) {
        words = "";
      }
      let wordsWithDecimal = "";
      if (isFloat) {
        if (!isNumberZero || !ignoreZero) {
          wordsWithDecimal += ` ${locale.texts.and} `;
        }
        const decimalLengthWord =
          locale?.decimalLengthWordMapping?.[split[1].length];
        wordsWithDecimal += `${this.convertInternal(
          Number(split[1]) *
            (!locale.decimalLengthWordMapping
              ? Math.pow(10, 2 - split[1].length)
              : 1),
          options
        )}${decimalLengthWord ? ` ${decimalLengthWord}` : ""} ${
          locale.currency.fractionalUnit.plural
        }`;
      } else if (locale.decimalLengthWordMapping && words !== "") {
        words += ` ${locale.currency.fractionalUnit.plural}`;
      }
      const isEmpty = words.length <= 0 && wordsWithDecimal.length <= 0;
      return (
        (!isEmpty && isNegativeNumber ? `${locale.texts.minus} ` : "") +
        words +
        wordsWithDecimal +
        (!isEmpty && locale.texts.only ? ` ${locale.texts.only}` : "")
      );
    } else {
      const isNumberZero = number >= 0 && number < 1;
      const split = number.toString().split(".");
      const ignoreZero = isNumberZero && locale.options?.ignoreZeroInDecimals;
      const words =
        isFloat && ignoreZero
          ? ""
          : this.convertInternal(Number(split[0]), options);
      let wordsWithDecimal = "";

      if (isFloat) {
        const decimalLengthWord =
          locale?.decimalLengthWordMapping?.[split[1].length];
        if (!ignoreZero) wordsWithDecimal += ` ${locale.texts.point} `;
        if (split[1].startsWith("0") && !locale.decimalLengthWordMapping) {
          const zeroWords = [];
          for (const num of split[1]) {
            zeroWords.push(this.convertInternal(Number(num)));
          }
          wordsWithDecimal += zeroWords.join(" ");
        } else {
          wordsWithDecimal += `${this.convertInternal(
            Number(split[1]),
            options
          )}${decimalLengthWord ? ` ${decimalLengthWord}` : ""}`;
        }
      }
      const isEmpty = words.length <= 0 && wordsWithDecimal.length <= 0;
      return (
        (!isEmpty && isNegativeNumber ? `${locale.texts.minus} ` : "") +
        words +
        wordsWithDecimal
      );
    }
  }

  private convertInternal(number: number, options = {}): string {
    const locale = this.getLocale();
    const splitWord = locale.options?.splitWord
      ? `${locale.options?.splitWord} `
      : "";
    const pluralMark = locale.options?.pluralMark
      ? `${locale.options?.pluralMark}`
      : "";
    const match = locale.numberWordsMapping.find((elem) => {
      return number >= elem.number;
    });

    if (!match) {
      throw new Error(`Invalid Number "${number}"`);
    }

    let words = "";
    if (number <= 100 || (number < 1000 && locale.options?.namedLessThan1000)) {
      words += match.value;
      number -= match.number;
      if (number > 0) {
        words += ` ${splitWord}${this.convertInternal(number, options)}`;
      }
    } else {
      const quotient = Math.floor(number / match.number);
      const remainder = number % match.number;
      const matchValue =
        quotient > 1 &&
        locale.options?.pluralWords?.find((word) => word === match.value)
          ? match.value + pluralMark
          : match.value;
      if (remainder > 0) {
        if (quotient == 1 && locale.options?.ignoreOneForWords) {
          return `${matchValue} ${splitWord}${this.convertInternal(
            remainder,
            options
          )}`;
        } else {
          return `${this.convertInternal(
            quotient,
            options
          )} ${matchValue} ${splitWord}${this.convertInternal(
            remainder,
            options
          )}`;
        }
      } else {
        if (quotient == 1 && locale.options?.ignoreOneForWords) {
          return `${matchValue}`;
        } else {
          return `${this.convertInternal(quotient, options)} ${matchValue}`;
        }
      }
    }
    return words;
  }

  toFixed(number: number, precision = 2): number {
    return Number(Number(number).toFixed(precision));
  }

  isFloat(number: number | string): boolean {
    return Number(number) === number && number % 1 !== 0;
  }

  isValidNumber(number: number | string): boolean {
    return !isNaN(parseFloat(number as string)) && isFinite(number as number);
  }
}

export class Locale implements LocaleInterface {
  public currency = {
    name: "Shilling",
    plural: "Shillings",
    symbol: "TZS",
    fractionalUnit: {
      name: "CENT",
      plural: "CENTS",
      symbol: "",
    },
  };

  public texts = {
    and: "And",
    minus: "Minus",
    only: "Only",
    point: "Point",
  };

  public numberWordsMapping = [
    { number: 1000000000000000000, value: "Quintillion" },
    { number: 1000000000000000, value: "Quadrillion" },
    { number: 1000000000000, value: "Trillion" },
    // ends here
    { number: 1000000000, value: "Billion" },
    { number: 1000000, value: "Million" },
    { number: 1000, value: "Thousand" },
    { number: 100, value: "Hundred" },
    { number: 90, value: "Ninety" },
    { number: 80, value: "Eighty" },
    { number: 70, value: "Seventy" },
    { number: 60, value: "Sixty" },
    { number: 50, value: "Fifty" },
    { number: 40, value: "Forty" },
    { number: 30, value: "Thirty" },
    { number: 20, value: "Twenty" },
    { number: 19, value: "Nineteen" },
    { number: 18, value: "Eighteen" },
    { number: 17, value: "Seventeen" },
    { number: 16, value: "Sixteen" },
    { number: 15, value: "Fifteen" },
    { number: 14, value: "Fourteen" },
    { number: 13, value: "Thirteen" },
    { number: 12, value: "Twelve" },
    { number: 11, value: "Eleven" },
    { number: 10, value: "Ten" },
    { number: 9, value: "Nine" },
    { number: 8, value: "Eight" },
    { number: 7, value: "Seven" },
    { number: 6, value: "Six" },
    { number: 5, value: "Five" },
    { number: 4, value: "Four" },
    { number: 3, value: "Three" },
    { number: 2, value: "Two" },
    { number: 1, value: "One" },
    { number: 0, value: "Zero" },
  ];
}
export interface LocaleInterface {
  currency: {
    name: string;
    plural: string;
    symbol: string;
    fractionalUnit: {
      name: string;
      plural: string;
      symbol: string;
    };
  };

  options?: {
    namedLessThan1000?: boolean;
    splitWord?: string;
    ignoreZeroInDecimals?: boolean;
    pluralMark?: string;
    pluralWords?: string[];
    ignoreOneForWords?: string[];
  };

  texts: {
    and: string;
    minus: string;
    only: string;
    point: string;
  };

  numberWordsMapping: {
    number: number;
    value: string;
  }[];

  decimalLengthWordMapping?: {
    [decimalLength: number]: string;
  };
}
type ConverterOptions = {
  currency?: boolean;
  ignoreDecimal?: boolean;
  ignoreZeroCurrency?: boolean;
};

const DefaultConverterOptions: ConverterOptions = {
  currency: false,
  ignoreDecimal: false,
  ignoreZeroCurrency: false,
};

type ToWordsOptions = {
  localeCode?: string;
  converterOptions?: ConverterOptions;
};

interface ConstructorOf<T> {
  new (): T;
}
